/**
 * CORE-M-04 Campaign Review tab body (Art's Q-6, answered 2026-09-22).
 *
 * Strict by design: the tab should be empty most of the time. A campaign is
 * flagged only when every rule holds (enough delivery, no big recent bid or
 * budget change, enough spend, and a poor result over BOTH 30 and 90 days);
 * brand campaigns never are; and while tracking has an open problem the tab
 * says so in one line and flags nothing. The rules live in the backend
 * (campaign_review_rules.py); this file only shows the answer.
 *
 * Actions per row (Jacob, 2026-09-24):
 *   - Raise with client: marks the row raised (who, when) and copies a plain
 *     30/90-day summary to paste to the client. Not Pause: Art's action is to
 *     raise it, because some clients want a service advertised whatever it
 *     returns. Pause stays in Campaign structure below.
 *   - Ignore: hides the row for this session.
 *   - Snooze 30 days with a one-tap reason (wrong / not now / need to check).
 *     It comes back early if the campaign's spend doubles.
 *
 * File name is deliberate: `CampaignReviewTab.tsx` mirrors the tab label.
 */
import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EvidenceRow } from '@/components/google-ads/EvidenceRow';
import { stickyToast } from '@/features/ads/stickyToast';
import { useResolvedRows } from '@/features/ads/useResolvedRows';
import {
  campaignReviewQueryKey,
  getCampaignReview,
  raiseCampaignWithClient,
  snoozeCampaignReview,
  type CampaignReviewRow,
  type CampaignReviewSnoozeReason,
} from '@/services/googleAdsCampaignReviewService';
import { useTenantStore } from '@/stores/useTenantStore';

import {
  MARKS_UNAVAILABLE,
  PAUSE_HINT,
  SNOOZE_REASON_LABEL,
  TAB_INTRO,
  checkedLine,
  clientSummary,
  conversions,
  conversionsColumn,
  conversionsNoun,
  markErrorText,
  noneNeedActionLine,
  notCheckedLines,
  raisedLine,
  ruleLabel,
  snoozedLine,
  targetLabel,
  trackingQuietLine,
  usd,
} from './campaignReviewCopy';

interface CampaignReviewTabProps {
  tenantId?: string | null;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A control with its disabled reason on hover and for screen readers. */
function WithReason({
  reason,
  children,
}: {
  reason: string | null;
  children: (describedBy: string | undefined) => ReactNode;
}) {
  const id = useId();
  return (
    <span title={reason ?? undefined} className="inline-flex">
      {children(reason ? id : undefined)}
      {reason ? (
        <span id={id} className="sr-only">
          {reason}
        </span>
      ) : null}
    </span>
  );
}

function WindowsTable({ row }: { row: CampaignReviewRow }) {
  return (
    <table className="w-full max-w-xl border-collapse text-left text-xs" data-testid="review-windows">
      <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
        <tr>
          <th className="py-1 pr-3 font-semibold">Period</th>
          <th className="py-1 pr-3 text-right font-semibold">Spend</th>
          <th className="py-1 pr-3 text-right font-semibold">Clicks</th>
          <th className="py-1 pr-3 text-right font-semibold">{conversionsColumn(row.conversion_rule)}</th>
          <th className="py-1 pr-3 text-right font-semibold">CPA</th>
          <th className="py-1 text-right font-semibold">Target</th>
        </tr>
      </thead>
      <tbody>
        {row.windows.map((w) => (
          <tr key={w.days} className="border-t border-border/50">
            <td className="py-1 pr-3">Last {w.days} days</td>
            <td className="py-1 pr-3 text-right tabular-nums">{usd(w.spend_usd)}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{w.clicks.toLocaleString('en-US')}</td>
            <td className="py-1 pr-3 text-right tabular-nums">{conversions(w.qualified_conversions)}</td>
            <td className="py-1 pr-3 text-right tabular-nums">
              {w.cpa_usd === null ? 'No conversions' : usd(w.cpa_usd)}
            </td>
            <td className="py-1 text-right tabular-nums">{usd(row.target_cpa_usd)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReviewRowCard({
  row,
  marksAvailable,
  busy,
  onRaise,
  onIgnore,
  onSnooze,
}: {
  row: CampaignReviewRow;
  marksAvailable: boolean;
  busy: boolean;
  onRaise: (row: CampaignReviewRow) => void;
  onIgnore: (row: CampaignReviewRow) => void;
  onSnooze: (row: CampaignReviewRow, reason: CampaignReviewSnoozeReason) => void;
}) {
  const [choosingReason, setChoosingReason] = useState(false);
  const short = row.windows[0];
  const long = row.windows[1];
  const blocked = marksAvailable ? null : MARKS_UNAVAILABLE;

  return (
    <div
      className="rounded-md border border-border bg-card p-4"
      data-testid="campaign-review-row"
      aria-busy={busy || undefined}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium">{row.campaign_name}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {targetLabel(row.target_cpa_usd, row.target_source, row.campaign_strategy, row.portfolio_unreadable)}{' '}
            {ruleLabel(row.conversion_rule)}
          </div>
          {row.raised ? (
            <Badge variant="outline" className="mt-2 border-border bg-muted/40 text-muted-foreground">
              {raisedLine(row.raised.by, row.raised.at)}
            </Badge>
          ) : null}
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden="true" />}
          <WithReason reason={blocked}>
            {(describedBy) => (
              <Button
                type="button"
                size="sm"
                disabled={busy || blocked !== null}
                aria-describedby={describedBy}
                onClick={() => onRaise(row)}
              >
                Raise with client
              </Button>
            )}
          </WithReason>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onIgnore(row)}>
            Ignore
          </Button>
          {choosingReason ? (
            <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Why snooze this campaign?">
              {(Object.keys(SNOOZE_REASON_LABEL) as CampaignReviewSnoozeReason[]).map((reason) => (
                <Button
                  key={reason}
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => {
                    setChoosingReason(false);
                    onSnooze(row, reason);
                  }}
                >
                  {SNOOZE_REASON_LABEL[reason]}
                </Button>
              ))}
            </div>
          ) : (
            <WithReason reason={blocked}>
              {(describedBy) => (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || blocked !== null}
                  aria-describedby={describedBy}
                  onClick={() => setChoosingReason(true)}
                >
                  Snooze 30 days
                </Button>
              )}
            </WithReason>
          )}
        </div>
      </div>

      {short && long ? (
        <div className="mt-3 space-y-2">
          <EvidenceRow
            evidence={{
              spend: usd(short.spend_usd),
              window: `last ${short.days} days`,
              conversions: `${conversions(short.qualified_conversions)} ${conversionsNoun(row.conversion_rule)}`,
              // The backend always writes a reason for a flagged row; the
              // fallback only keeps one bad row from breaking the tab.
              reason: row.reason || 'Flagged by the campaign review rules.',
            }}
            additionalChips={[
              {
                label: short.cpa_usd === null ? 'No conversions' : `CPA ${usd(short.cpa_usd)}`,
                tone: 'warning',
                title: `Target CPA ${usd(row.target_cpa_usd)}`,
              },
              {
                label: `90 days: ${usd(long.spend_usd)}, ${conversions(long.qualified_conversions)} conv.`,
                title: 'Spend and qualified conversions over the last 90 days',
              },
            ]}
          />
          <WindowsTable row={row} />
        </div>
      ) : null}
    </div>
  );
}

export function CampaignReviewTab({ tenantId: tenantIdProp }: CampaignReviewTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const queryClient = useQueryClient();
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const q = useQuery({
    queryKey: campaignReviewQueryKey(tenantId),
    queryFn: () => getCampaignReview(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const { visibleRows, resolve } = useResolvedRows(q.data?.flagged ?? [], {
    getRowId: (r) => r.campaign_resource_name,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: campaignReviewQueryKey(tenantId) });

  const raise = useMutation({
    mutationFn: (row: CampaignReviewRow) =>
      raiseCampaignWithClient(tenantId ?? '', row.campaign_resource_name),
    onMutate: (row) => setBusyRow(row.campaign_resource_name),
    onSuccess: async (_mark, row) => {
      const copied = await copyText(clientSummary(row));
      stickyToast.success(
        copied
          ? `Marked ${row.campaign_name} as raised with the client. The summary is copied; paste it to the client.`
          : `Marked ${row.campaign_name} as raised with the client. Your browser blocked copying, so copy the numbers from the row by hand.`,
      );
      await refresh();
    },
    onError: (err, row) => {
      stickyToast.error(`Could not mark ${row.campaign_name} as raised. ${markErrorText(err)}`);
    },
    onSettled: () => setBusyRow(null),
  });

  const snooze = useMutation({
    mutationFn: ({ row, reason }: { row: CampaignReviewRow; reason: CampaignReviewSnoozeReason }) =>
      snoozeCampaignReview(tenantId ?? '', row.campaign_resource_name, reason),
    onMutate: ({ row }) => setBusyRow(row.campaign_resource_name),
    onSuccess: async (_mark, { row }) => {
      stickyToast.success(
        `Snoozed ${row.campaign_name} for 30 days. It comes back early if its spend doubles.`,
      );
      await refresh();
    },
    onError: (err, { row }) => {
      stickyToast.error(`Could not snooze ${row.campaign_name}. ${markErrorText(err)}`);
    },
    onSettled: () => setBusyRow(null),
  });

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>Pick a workspace to see its campaign review.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking campaigns for {tenantId}...
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not check campaigns</CardTitle>
          <CardDescription>
            Core could not read campaigns for{' '}
            <span className="font-medium text-foreground">{tenantId}</span>. Check
            the Google Ads connection in Settings, under Integrations, then reload.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const data = q.data;

  if (!data.read_ok) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not check campaigns</CardTitle>
          <CardDescription>
            {data.message || 'Core could not read this account from Google Ads.'} Try
            again in a minute.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (data.tracking_quiet) {
    return (
      <Card data-testid="campaign-review-quiet">
        <CardHeader>
          <CardTitle>Campaign Review</CardTitle>
          <CardDescription>{trackingQuietLine(data.tracking_quiet)}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const notChecked = notCheckedLines(data.not_checked ?? {});

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Campaign Review</CardTitle>
          <CardDescription>{TAB_INTRO}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 pt-0 text-sm text-muted-foreground">
          <p>{checkedLine(data.evaluated_count)}</p>
          {notChecked.map((line) => (
            <p key={line}>{line}</p>
          ))}
          <p>{PAUSE_HINT}</p>
        </CardContent>
      </Card>

      {visibleRows.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No campaigns need action</CardTitle>
            <CardDescription>{noneNeedActionLine(data.evaluated_count)}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-3">
          {visibleRows.map((row) => (
            <ReviewRowCard
              key={row.campaign_resource_name}
              row={row}
              marksAvailable={data.marks_available}
              busy={busyRow === row.campaign_resource_name}
              onRaise={(r) => {
                if (busyRow) return;
                raise.mutate(r);
              }}
              onIgnore={(r) => resolve(r.campaign_resource_name)}
              onSnooze={(r, reason) => {
                if (busyRow) return;
                snooze.mutate({ row: r, reason });
              }}
            />
          ))}
        </div>
      )}

      {data.snoozed.length > 0 ? (
        <details className="rounded-md border border-border p-3 text-sm" data-testid="campaign-review-snoozed">
          <summary className="cursor-pointer text-muted-foreground">
            {data.snoozed.length} snoozed
          </summary>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            {data.snoozed.map((row) => (
              <li key={row.campaign_resource_name}>{snoozedLine(row)}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export default CampaignReviewTab;
